import { createContext, PropsWithChildren, useContext } from 'react';
import { ToastService } from './toast.service';
import { useTranslation } from 'react-i18next';
import { toastConfig, ToastType } from './toast.config';
import { ToastServiceInterface } from './toast-service.interface';

type ToastContextType = {
    show: (type: ToastType) => void;
};

const ToastContext = createContext<ToastContextType | null>(null);

const ToastProvider = ({ children }: PropsWithChildren) => {
    const toastService: ToastServiceInterface = new ToastService();
    const { t } = useTranslation();

    const show = (toastType: ToastType) => {
        const config = toastConfig[toastType];
        if (!config) {
            console.warn(`No toast configuration found for type: ${toastType}`);
            return;
        }
        const message = t(config.translationKey);
        toastService.show(message, {
            type: config.options.type,
            id: toastType,
        });
        if (process.env.NODE_ENV === 'development') {
            console.log(`Toast shown: ${toastType}`, config);
        }
    };

    return (
        <ToastContext.Provider value={{ show }}>
            {toastService.getComponent()}
            {children}
        </ToastContext.Provider>
    );
};

export const useToastContext = () => {
    const ctx = useContext(ToastContext);
    if (!ctx) {
        // throwing here is a nice touch because this is a developer error
        // and if this happened during production, something must have really broken
        throw new Error(
            'useToastContext can only be used in a ToastContext-Provider tree'
        );
    }
    return ctx;
};

export default ToastProvider;
